// Utility functions for processing markdown and text content
import katex from 'katex';

export const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// NOTE: all generated HTML in this file must be SINGLE-LINE. The renderer
// converts residual source newlines to <br>/spacers as the final step, so a
// newline inside a generated tag/attribute would corrupt the markup.
export const processInlineCode = (content: string): string => {
  return content.replace(/`([^`]+)`/g, (match, code) => {
    return `<code style="background:var(--pa-panel2, rgba(140,140,150,0.14));border:1px solid var(--pa-line, rgba(140,140,150,0.18));padding:1px 5px;border-radius:5px;font-family:'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:0.85em;color:var(--pa-ink, currentColor);">${escapeHtml(code)}</code>`;
  });
};

// Standard markdown images: `![alt](url)` → bordered block <img> that links
// out to the full image. Only http(s) URLs are converted; anything else is
// left as literal text. MUST run before processMarkdownLinks - its [text](url)
// regex would otherwise eat the [alt](url) tail and leave a stray "!".
// The IndexedDB attachment flows ([File: ...](file:...) and [image]...[/image])
// use different syntax and are untouched.
export const processMarkdownImages = (text: string): string => {
  return text.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (match, alt, url) => {
    if (!/^https?:\/\//i.test(url)) return match;
    const safeAlt = escapeHtml(alt || '');
    const safeUrl = url.replace(/"/g, '&quot;');
    // The <a> is a no-JS fallback; the React click delegation on .pa-body
    // intercepts clicks on .pa-md-img and opens the in-app lightbox instead.
    return (
      `<div style="margin:12px 0;max-width:420px;">` +
        `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="display:block;text-decoration:none;">` +
          `<img src="${safeUrl}" alt="${safeAlt}" loading="lazy" class="pa-md-img" ` +
            `style="display:block;max-width:100%;height:auto;border-radius:12px;border:1px solid var(--pa-line, rgba(140,140,150,0.2));cursor:zoom-in;" ` +
            `onerror="this.style.display='none';var f=this.nextElementSibling;if(f)f.style.display='inline-flex';">` +
          `<span style="display:none;align-items:center;gap:6px;font-size:11px;color:var(--pa-ink3, #8B9099);border:1px solid var(--pa-line, rgba(140,140,150,0.2));background:var(--pa-panel2, rgba(140,140,150,0.08));border-radius:7px;padding:4px 9px;">🖼 ${safeAlt || 'Gambar gagal dimuat'}</span>` +
        `</a>` +
      `</div>`
    );
  });
};

// Multiline `> quote` groups → styled <blockquote> instead of raw ">" text.
// Runs after thematic breaks and before lists. Inner newlines become <br>
// (the group is consumed whole, so the rhythm pass never sees them).
export const processBlockquotes = (text: string): string => {
  return text.replace(/^>[^\n]*(?:\n>[^\n]*)*/gm, (block) => {
    const inner = block
      .split('\n')
      .map((line) => line.replace(/^>[ \t]?/, ''))
      .join('<br>');
    return `<blockquote style="margin:10px 0;padding:2px 0 2px 14px;border-left:2px solid var(--pa-line2, rgba(140,140,150,0.3));color:var(--pa-ink2, inherit);">${inner}</blockquote>`;
  });
};

// `---` / `***` / `___` on their own line → proper hairline rule instead of
// literal text. Runs after table processing (separator rows start with `|`,
// so they never reach this) and before bold/italic (which would mangle ***).
export const processThematicBreaks = (text: string): string => {
  return text.replace(
    /^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm,
    '<hr style="border:none;border-top:1px solid var(--pa-line, rgba(140,140,150,0.25));margin:16px 0;">'
  );
};

export const sanitizeFilename = (filename: string): string => {
  // Replace brackets and other special characters that might cause issues in markdown links
  return filename
    .replace(/\[/g, '(')  // Replace [ with (
    .replace(/\]/g, ')'); // Replace ] with )
};

export const processThinkingBlocks = (text: string, theme: 'light' | 'dark'): string => {
  return text.replace(/<think>([\s\S]*?)(?:<\/think>|$)/g, (_, content) => {
    // Escape HTML to prevent any other markdown or code rendering
    const escapedContent = escapeHtml(content);
    
    return `<div style="
      margin: 8px 0;
      padding: 12px 16px;
      border-radius: 6px;
      background-color: ${theme === 'light' ? '#F0F4F9' : '#1E2937'};
      border-left: 4px solid ${theme === 'light' ? '#3B82F6' : '#60A5FA'};
      color: ${theme === 'light' ? '#1F2937' : '#E5E7EB'};
      font-size: 0.95em;
      font-style: italic;
    ">
      <div style="
        margin-bottom: 4px;
        font-size: 0.85em;
        color: ${theme === 'light' ? '#4B5563' : '#9CA3AF'};
        font-weight: 500;
      ">Thinking process:</div>
      <pre style="
        font-family: inherit;
        white-space: pre-wrap;
        word-wrap: break-word;
        margin: 0;
      ">${escapedContent}</pre>
    </div>`;
  });
};

export const sanitizeHtmlTags = (text: string): string => {
  return text.replace(/<([^>]*)>/g, (match) => {
    // Allow specific safe HTML tags that we want to keep
    if (/^<\/?(?:b|i|em|strong|br|p|ul|ol|li|h[1-6]|hr|blockquote|a|img|code|pre|table|tr|td|th|div)(?:\s|\/?>|>)/.test(match)) {
      return match;
    }
    // Escape all other HTML tags
    return escapeHtml(match);
  });
};

export const processMarkdownLinks = (text: string): string => {
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    // Handle file links
    if (url.startsWith('file:')) {
      return `<span style="background:rgba(140,140,150,0.14);border:1px solid rgba(140,140,150,0.18);color:inherit;padding:2px 10px;border-radius:999px;font-size:0.85em;font-weight:500;display:inline-block;margin:2px;text-decoration:none;">📎 ${linkText}</span>`;
    }

    // Web presentation deck: a self-contained HTML slide deck served from
    // `/uploads/presentations/<slug>-<hex>.html` becomes a LIVE preview card
    // (class `deck-card`) whose whole surface is the anchor. MUST run BEFORE the
    // .pptx branch below: a .html deck gets this card, a .pptx link keeps the old
    // download card. The two hrefs are mutually exclusive (.html vs .pptx), but
    // the order is load-bearing so neither can ever fall into the other's branch.
    // NO `download` attr - the deck opens in a new tab. The iframe is laid out at
    // 200% then scaled 0.5 so the real cover slide shows at a readable size;
    // pointer-events:none routes the click to the card, not the deck. Panel/line/
    // ink/ink3 read the same token vars every other block here uses. MUST stay
    // single-line (see the note at the top of this file).
    if (/\/uploads\/presentations\/.+\.html(?:$|[?#])/i.test(url)) {
      const safeUrl = url.replace(/"/g, '&quot;');
      const fallbackName = url.split('?')[0].split('#')[0].split('/').pop() || 'presentasi.html';
      let rawName = (linkText && linkText.trim()) ? linkText.trim() : fallbackName;
      if (rawName === fallbackName) {
        try { rawName = decodeURIComponent(rawName); } catch { /* keep raw */ }
      }
      const name = escapeHtml(rawName);
      return (
        `<div style="margin:12px 0;max-width:560px;">` +
          `<a href="${safeUrl}" class="deck-card" aria-label="${name}" target="_blank" rel="noopener noreferrer" ` +
            `style="display:block;border-radius:14px;border:1px solid var(--pa-line, rgba(140,140,150,0.2));overflow:hidden;text-decoration:none;transition:border-color 0.2s ease;" ` +
            `onmouseover="this.style.borderColor='var(--pa-line2, rgba(140,140,150,0.35))'" onmouseout="this.style.borderColor='var(--pa-line, rgba(140,140,150,0.2))'">` +
            `<span style="display:block;position:relative;width:100%;aspect-ratio:16/9;border-radius:14px 14px 0 0;overflow:hidden;background:#0A0B0D;">` +
              `<iframe src="${safeUrl}#1" loading="lazy" sandbox="allow-scripts allow-same-origin" style="width:200%;height:200%;transform:scale(0.5);transform-origin:0 0;border:0;pointer-events:none;" tabindex="-1"></iframe>` +
              `<span style="position:absolute;right:8px;bottom:8px;background:rgba(0,0,0,0.55);color:#fff;font-size:12px;padding:3px 9px;border-radius:999px;">Buka ↗</span>` +
            `</span>` +
            // Footer is a single generic caption: the cover slide in the iframe
            // already shows the deck's own title, so repeating it here was pure
            // duplication. The title stays accessible via the anchor's aria-label.
            `<span style="display:flex;flex-direction:column;gap:2px;padding:11px 13px;background:var(--pa-panel2, rgba(140,140,150,0.08));border-top:1px solid var(--pa-line, rgba(140,140,150,0.2));">` +
              `<span style="font-size:13px;font-weight:500;color:var(--pa-ink, currentColor);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Presentasi · buka di tab baru</span>` +
            `</span>` +
          `</a>` +
        `</div>`
      );
    }

    // Children's comic viewer: `/uploads/comics/<slug>-<hex>.html` gets the same
    // live-preview treatment as the deck-card, with a cream backdrop matching the
    // viewer's paper theme and a "Komik" caption. Mutually exclusive with the
    // presentations path by URL prefix. MUST stay single-line (note at top).
    if (/\/uploads\/comics\/.+\.html(?:$|[?#])/i.test(url)) {
      const safeUrl = url.replace(/"/g, '&quot;');
      const fallbackName = url.split('?')[0].split('#')[0].split('/').pop() || 'komik.html';
      let rawName = (linkText && linkText.trim()) ? linkText.trim() : fallbackName;
      if (rawName === fallbackName) {
        try { rawName = decodeURIComponent(rawName); } catch { /* keep raw */ }
      }
      const name = escapeHtml(rawName);
      return (
        `<div style="margin:12px 0;max-width:560px;">` +
          `<a href="${safeUrl}" class="comic-card" aria-label="${name}" target="_blank" rel="noopener noreferrer" ` +
            `style="display:block;border-radius:14px;border:1px solid var(--pa-line, rgba(140,140,150,0.2));overflow:hidden;text-decoration:none;transition:border-color 0.2s ease;" ` +
            `onmouseover="this.style.borderColor='var(--pa-line2, rgba(140,140,150,0.35))'" onmouseout="this.style.borderColor='var(--pa-line, rgba(140,140,150,0.2))'">` +
            `<span style="display:block;position:relative;width:100%;aspect-ratio:16/10;border-radius:14px 14px 0 0;overflow:hidden;background:#FFF8EE;">` +
              `<iframe src="${safeUrl}" loading="lazy" sandbox="allow-scripts allow-same-origin" style="width:200%;height:200%;transform:scale(0.5);transform-origin:0 0;border:0;pointer-events:none;" tabindex="-1"></iframe>` +
              `<span style="position:absolute;right:8px;bottom:8px;background:rgba(0,0,0,0.55);color:#fff;font-size:12px;padding:3px 9px;border-radius:999px;">Buka ↗</span>` +
            `</span>` +
            `<span style="display:flex;flex-direction:column;gap:2px;padding:11px 13px;background:var(--pa-panel2, rgba(140,140,150,0.08));border-top:1px solid var(--pa-line, rgba(140,140,150,0.2));">` +
              `<span style="font-size:13px;font-weight:500;color:var(--pa-ink, currentColor);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Komik · buka di tab baru</span>` +
            `</span>` +
          `</a>` +
        `</div>`
      );
    }

    // Presentation downloads: a normal markdown link whose href ends in `.pptx`
    // (case-insensitive, querystring/hash tolerated) becomes an elegant download
    // card instead of a plain anchor - the whole card is the anchor. Jade is
    // hardcoded like the regular-link path (no --pa-jade var exists on .pa-body);
    // panel/line/ink/ink3 read the same token vars every other block here uses,
    // so both themes stay token-driven. Wrapped in a block <div> like markdown
    // images so the final rhythm pass normalizes its margins. MUST stay single-
    // line (see the note at the top of this file).
    if (/\.pptx(?:$|[?#])/i.test(url)) {
      const safeUrl = url.replace(/"/g, '&quot;');
      const fallbackName = url.split('?')[0].split('#')[0].split('/').pop() || 'presentasi.pptx';
      let rawName = (linkText && linkText.trim()) ? linkText.trim() : fallbackName;
      if (rawName === fallbackName) {
        try { rawName = decodeURIComponent(rawName); } catch { /* keep raw */ }
      }
      const name = escapeHtml(rawName);
      const JADE = '#5BD6A9';
      const JADE_DIM = 'rgba(91,214,169,0.12)';
      return (
        `<div style="margin:12px 0;max-width:420px;">` +
          `<a href="${safeUrl}" class="pptx-card" target="_blank" rel="noopener noreferrer" download ` +
            `style="display:flex;align-items:center;gap:12px;padding:11px 13px;border-radius:12px;background:var(--pa-panel2, rgba(140,140,150,0.08));border:1px solid var(--pa-line, rgba(140,140,150,0.2));text-decoration:none;transition:border-color 0.2s ease;" ` +
            `onmouseover="this.style.borderColor='var(--pa-line2, rgba(140,140,150,0.35))'" onmouseout="this.style.borderColor='var(--pa-line, rgba(140,140,150,0.2))'">` +
            `<span style="flex-shrink:0;width:36px;height:36px;border-radius:9px;background:${JADE_DIM};display:inline-flex;align-items:center;justify-content:center;">` +
              `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${JADE}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h20"/><path d="M3 3v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V3"/><path d="m8 21 4-4 4 4"/><path d="M12 15v2"/></svg>` +
            `</span>` +
            `<span style="display:flex;flex-direction:column;flex:1;min-width:0;gap:2px;">` +
              `<span style="font-size:14px;font-weight:600;color:var(--pa-ink, currentColor);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name}</span>` +
              `<span style="font-size:12px;color:var(--pa-ink3, #8B9099);">Presentasi PowerPoint · unduh</span>` +
            `</span>` +
            `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--pa-ink3, #8B9099)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg>` +
          `</a>` +
        `</div>`
      );
    }

    // Regular links
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#5BD6A9;font-weight:500;text-decoration:none;border-bottom:1px solid rgba(91,214,169,0.35);transition:all 0.2s ease;" onmouseover="this.style.borderBottomColor='#5BD6A9'" onmouseout="this.style.borderBottomColor='rgba(91,214,169,0.35)'">${linkText}</a>`;
  });
};

export const processMarkdownFormatting = (text: string): string => {
  // ***bold italic*** first - the plain ** pass would otherwise split the
  // triple markers into overlapping <strong>/<em> with malformed nesting.
  text = text.replace(/\*\*\*(.+?)\*\*\*/g, '<strong style="font-weight:600;color:var(--pa-ink, currentColor);"><em style="font-style:italic;">$1</em></strong>');

  // Bold pops to full ink (Obsidian contrast); body text sits at the dimmer scale.
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:600;color:var(--pa-ink, currentColor);">$1</strong>');

  // Process italic text
  text = text.replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>');

  return text;
};

export const processLists = (text: string): string => {
  // Tag items with their intended list type FIRST, then wrap each same-type run
  // exactly once. (The old two-pass version re-matched the <li>s it had already
  // wrapped in <ol> and nested them inside a second <ul>, doubling indent and
  // margins - one of the main sources of the ragged vertical rhythm.)
  // Stamp each ordered item with its SOURCE number via `value="N"`. When a
  // bullet sub-list splits a numbered run into multiple <ol>s (see wrapRuns),
  // the second <ol> would otherwise restart at 1, but browsers honor <li value>
  // across separate <ol>s, so the original numbering survives the split. The
  // ` data-md="ol"` strip in wrapRuns leaves `value=` intact.
  text = text.replace(/^[ \t]*(\d+)[.)][ \t]+(.+)$/gm, '<li data-md="ol" value="$1" style="margin:4px 0;">$2</li>');
  text = text.replace(/^[ \t]*[•\-\*][ \t]+(.+)$/gm, '<li data-md="ul" style="margin:4px 0;">$1</li>');

  const wrapRuns = (type: 'ol' | 'ul') => {
    const re = new RegExp(`(?:<li data-md="${type}"[^>]*>[\\s\\S]*?<\\/li>\\n?)+`, 'g');
    text = text.replace(re, (run) => {
      const items = run.replace(/\n/g, '').replace(new RegExp(` data-md="${type}"`, 'g'), '');
      // CRITICAL: re-emit one trailing newline. The run pattern's `\n?` swallows
      // the newline after the LAST <li> - i.e. the boundary between the list and
      // whatever follows it. That is the same "swallowed line anchor" trap the
      // table generator fixes: any line-anchored pass running after processLists
      // (or the final rhythm pass's block-boundary detection) needs that \n to
      // keep the following source line separate. The rhythm pass strips it again
      // after the </ul>/</ol> block tag, so the final HTML is unchanged.
      return `<${type} style="margin:10px 0 14px;padding-left:20px;list-style-type:${type === 'ol' ? 'decimal' : 'disc'};">${items}</${type}>` + '\n';
    });
  };
  wrapRuns('ol');
  wrapRuns('ul');

  return text;
};

export const processHeadings = (text: string, theme: 'light' | 'dark'): string => {
  // Obsidian chat headings: clamped scale (h1 19 / h2 17 / h3 15), tight top
  // margin, small bottom margin. Color reads the message-level token var with
  // a per-theme fallback for contexts outside .pa-body.
  const ink = theme === 'light' ? '#16181C' : '#F2F3F5';
  const h = (tag: string, size: string, weight: number, mt: string, mb: string, content: string) =>
    `<${tag} style="font-size:${size};font-weight:${weight};letter-spacing:-0.015em;margin:${mt} 0 ${mb};padding:0;color:var(--pa-ink, ${ink});line-height:1.35;">${processInlineCode(content)}</${tag}>`;

  // Deepest markers FIRST. Each pattern demands a space immediately after its
  // exact hash count, so `##### x` never matches the `### ` rule. Without the
  // h4-h6 rules those markers used to survive as literal "#### text" (a raw
  // markdown token leaking into the output).
  text = text.replace(/^###### (.+)$/gm, (_, content) => h('h6', '13px', 600, '14px', '4px', content));
  text = text.replace(/^##### (.+)$/gm, (_, content) => h('h5', '13px', 600, '14px', '4px', content));
  text = text.replace(/^#### (.+)$/gm, (_, content) => h('h4', '14px', 600, '15px', '5px', content));
  text = text.replace(/^### (.+)$/gm, (_, content) => h('h3', '15px', 600, '16px', '5px', content));
  text = text.replace(/^## (.+)$/gm, (_, content) => h('h2', '17px', 650, '18px', '6px', content));
  text = text.replace(/^# (.+)$/gm, (_, content) => h('h1', '19px', 650, '18px', '6px', content));

  return text;
};

// A divider-row cell: ---, :---, ---:, :---: with optional surrounding spaces.
// Three dashes minimum so data-row placeholder cells like "-" or "--" are never
// mistaken for divider cells by the merged-line splitter below.
const SEPARATOR_CELL = /^\s*:?-{3,}:?\s*$/;

// Models sometimes glue the divider row and the FIRST DATA ROW into one line:
//   `|:---:|:---:|---|---|| 1 | 2004 | **Joy** | Delon |`
// The table regex then sees no clean header+divider and gives up, leaving raw
// pipe soup. Detect a line that starts with >=2 divider cells and then
// continues with data (a `||` boundary glitch after the last divider cell, or
// simply the first non-divider cell), and split it back into divider line +
// data line before table parsing.
const splitMergedSeparatorRows = (content: string): string => {
  return content
    .split('\n')
    .map((line) => {
      const t = line.trim();
      if (!t.startsWith('|') || !t.includes('-')) return line;
      const tokens = t.split('|'); // tokens[0] === '' from the leading pipe
      let i = 1;
      while (i < tokens.length && SEPARATOR_CELL.test(tokens[i])) i++;
      const sepCount = i - 1;
      if (sepCount < 2) return line; // not a divider row
      if (i < tokens.length && tokens[i].trim() === '') i++; // consume the `||` glitch
      const rest = tokens.slice(i);
      const sepLine = '|' + tokens.slice(1, 1 + sepCount).join('|') + '|';
      if (!rest.some((tok) => tok.trim() !== '')) {
        // pure divider row (possibly with doubled/trailing pipe) - normalized
        return sepLine;
      }
      let data = rest.join('|').trim();
      if (!data.startsWith('|')) data = '| ' + data;
      if (!data.endsWith('|')) data = data + ' |';
      return sepLine + '\n' + data;
    })
    .join('\n');
};

// Last-resort presentation for pipe-heavy blocks that STILL failed to parse as
// a table (e.g. missing divider row): render each row as a plain line with the
// cell text joined by " · " instead of leaking raw `| a | b |` soup. Divider
// rows (dashes/colons only) are dropped. Conservative on purpose: only runs on
// >=2 CONSECUTIVE lines that each start AND end with a pipe - prose containing
// a single `|`, or one lone pipe line, is untouched.
const degradePipeRuns = (content: string): string => {
  const lines = content.split('\n');
  const isPipeRow = (l: string): boolean => {
    const t = l.trim();
    return t.startsWith('|') && t.endsWith('|') && (t.match(/\|/g) || []).length >= 2;
  };
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!isPipeRow(lines[i])) { out.push(lines[i]); i++; continue; }
    let j = i;
    while (j < lines.length && isPipeRow(lines[j])) j++;
    if (j - i < 2) { out.push(lines[i]); i = j; continue; } // single pipe line: leave as-is
    for (let k = i; k < j; k++) {
      const t = lines[k].trim();
      if (/^[|\s:-]+$/.test(t)) continue; // divider soup - drop entirely
      const cells = t.replace(/^\|/, '').replace(/\|$/, '').split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      out.push(cells.join(' · '));
    }
    i = j;
  }
  return out.join('\n');
};

// GFM-ish table → styled HTML block. Extracted here (with the whole pipeline)
// so the exact transformation is unit-testable outside React. Depends only on
// katex + the processors above.
const processTableBlock = (content: string, theme: 'light' | 'dark'): string => {
  // Repair glued divider+data lines first so the regex sees a clean table.
  content = splitMergedSeparatorRows(content);

  // Divider row accepts any mix of ---, :---, ---:, :---: plus spaces and
  // doubled pipes ([ \t:|-]; the old [-|] class rejected the colons of
  // aligned columns, which alone was enough to drop a whole table to soup).
  const tableRegex = /^\s*\|(.+)\|\s*\n\s*\|([ \t:|-]+)\|\s*\n((?:\s*\|.+\|\s*\n?)*)/gm;

  content = content.replace(tableRegex, (match) => {
    const rows = match
      .trim()
      .split('\n')
      .map(row => row.trim())
      .filter(row => row && row.startsWith('|') && row.endsWith('|'));

    if (rows.length < 3) return match;

    const headers = rows[0]
      .split('|')
      .slice(1, -1)
      .map(cell => cell.trim());

    // Divider cells; empty entries (from a doubled `||` glitch) are dropped
    // rather than treated as columns.
    const alignments = rows[1]
      .split('|')
      .slice(1, -1)
      .map(cell => cell.trim())
      .filter(cell => cell !== '');

    // Every divider cell must be a dash run with optional alignment colons,
    // otherwise this is not a real table; bail out so the caller's degrade
    // pass can salvage the block instead of us mis-rendering it.
    if (alignments.length === 0 || !alignments.every(a => /^:?-+:?$/.test(a))) {
      return match;
    }

    const dataRows = rows.slice(2)
      .map(row => row
        .split('|')
        .slice(1, -1)
        .map(cell => cell.trim())
      );

    // Get alignment for each column (pad with 'left' if the divider row has
    // fewer cells than the header - tolerated, not fatal).
    const aligns = headers.map((_, i) => {
      const align = alignments[i] || '';
      if (align.startsWith(':') && align.endsWith(':')) return 'center';
      if (align.endsWith(':')) return 'right';
      return 'left';
    });

    // Function to process cell content
    const processCellContent = (cellContent: string) => {
      // Process \times symbol
      cellContent = cellContent.replace(/\\times/g, '×');

      // Process LaTeX math expressions
      cellContent = cellContent.replace(/\\\((.+?)\\\)/g, (_, expr) => {
        try {
          return katex.renderToString(expr.trim(), {
            displayMode: false,
            throwOnError: false,
            output: 'html'
          });
        } catch (error) {
          console.error('KaTeX error:', error);
          return `\\(${expr}\\)`; // Return original format on error
        }
      });

      // Process bold text
      cellContent = cellContent.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');

      // Process inline code
      return processInlineCode(cellContent);
    };

    const hairline = theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)';
    const headBg = theme === 'light' ? '#F1F2F4' : 'rgba(255,255,255,0.03)';
    const headColor = theme === 'light' ? '#8B9099' : '#5C6068';
    const cellColor = theme === 'light' ? '#3A3D42' : '#C9CCD2';
    const html = `
      <div style="overflow-x: auto; margin: 12px 0; border: 1px solid ${hairline}; border-radius: 10px;">
        <table style="
          border-collapse: separate;
          border-spacing: 0;
          width: 100%;
          font-size: 13px;
          text-align: left;
        ">
          <thead>
            <tr style="background-color: ${headBg};">
              ${headers.map((header, i) => `
                <th style="
                  padding: 8px 12px;
                  text-align: ${aligns[i]};
                  font-weight: 600;
                  font-size: 10.5px;
                  letter-spacing: 0.07em;
                  text-transform: uppercase;
                  color: ${headColor};
                  white-space: nowrap;
                  border-bottom: 1px solid ${hairline};
                ">${processCellContent(header)}</th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${dataRows.map((row, rowIndex) => `
              <tr>
                ${row.map((cell, i) => `
                  <td style="
                    padding: 8px 12px;
                    text-align: ${aligns[i]};
                    color: ${cellColor};
                    white-space: nowrap;
                    border-bottom: 1px solid ${rowIndex === dataRows.length - 1 ? 'transparent' : hairline};
                  ">${processCellContent(cell)}</td>
                `).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    // Single-line the generated markup - the final rhythm pass converts
    // remaining newlines to <br>, which must never land inside a tag.
    // CRITICAL: re-emit one trailing newline. The row pattern's `\s*\n?`
    // swallows every newline after the last table row, which would leave
    // following line-anchored markdown (### headings, - lists, ---) glued
    // to </div> mid-line where the ^-anchored regexes can no longer see it
    // (the "literal ###" bug). The restored \n keeps the line boundary;
    // the final rhythm pass strips it again after block tags anyway.
    return html.replace(/\s*\n\s*/g, '') + '\n';
  });

  // Whatever pipe-heavy blocks survived the regex could not be parsed as a
  // table - present them without raw pipe soup.
  return degradePipeRuns(content);
};

// Full assistant-markdown → HTML transformation. This is the SINGLE SOURCE OF
// TRUTH for the render used by the chat (contentRendering.tsx delegates here)
// and by the regression harness (scripts/test-markdown.mjs). Every generated
// tag is single-line; the vertical-rhythm pass at the end is the only place
// that turns residual source newlines into <br>/spacers/block boundaries.
export const renderMarkdownToHtml = (
  text: string,
  theme: 'light' | 'dark',
  citationsMap?: { [key: string]: any }
): string => {
  if (!text.trim()) return '';

  // Process citations first if available
  if (citationsMap) {
    text = text.replace(/\[(\d+)\]/g, (match, num) => {
      const sourceIndex = parseInt(num) - 1;
      const sourceArray = Object.keys(citationsMap);
      const source = sourceArray[sourceIndex];
      if (source) {
        return `<a href="${source}">[${num}]</a>`;
      }
      return match;
    });
  }

  const processMarkdown = (input: string): string => {
    // Sanitize HTML tags
    input = sanitizeHtmlTags(input);

    // Markdown images - must precede link processing (see processMarkdownImages)
    input = processMarkdownImages(input);

    // Process markdown links
    input = processMarkdownLinks(input);

    // Process inline code
    input = processInlineCode(input);

    // Process tables before other markdown elements
    input = processTableBlock(input, theme);

    // Process headers
    input = processHeadings(input, theme);

    // Thematic breaks (--- / *** / ___) → hairline <hr>. After tables (their
    // separator rows start with '|') and before bold/italic (*** would clash).
    input = processThematicBreaks(input);

    // Blockquotes (`> ...` line groups) - before lists so quoted "- " lines
    // stay inside the quote instead of becoming list items.
    input = processBlockquotes(input);

    // Process lists
    input = processLists(input);

    // Process common mathematical symbols before math expressions
    input = input.replace(/\\times/g, '×');
    input = input.replace(/\\pm/g, '±');
    input = input.replace(/\\div/g, '÷');
    input = input.replace(/\\leq/g, '≤');
    input = input.replace(/\\geq/g, '≥');
    input = input.replace(/\\neq/g, '≠');
    input = input.replace(/\\approx/g, '≈');
    input = input.replace(/\\alpha/g, 'α');
    input = input.replace(/\\beta/g, 'β');
    input = input.replace(/\\gamma/g, 'γ');
    input = input.replace(/\\delta/g, 'δ');
    input = input.replace(/\\pi/g, 'π');
    input = input.replace(/\\theta/g, 'θ');
    input = input.replace(/\\lambda/g, 'λ');
    input = input.replace(/\\mu/g, 'μ');
    input = input.replace(/\\sigma/g, 'σ');
    input = input.replace(/\\infty/g, '∞');

    // Process bold/italic
    input = processMarkdownFormatting(input);

    // Process block math
    input = input.replace(/\$\$([\s\S]+?)\$\$/g, (_, expression) => {
      try {
        const html = katex.renderToString(expression.trim(), {
          displayMode: true,
          throwOnError: false,
          output: 'html'
        });
        return `<div style="margin: 12px 0; text-align: center;">${html}</div>`;
      } catch (error) {
        console.error('KaTeX error:', error);
        return `<div style="margin: 12px 0; text-align: center; font-family: monospace;">$$${expression}$$</div>`;
      }
    });

    // Process inline math - improved regex to avoid matching numbers and monetary values
    input = input.replace(/\$(?!\d)(?![0-9,.\s\\!]+(?:\s*\+|\s*-|\s*=|\s*\$|$))([^$\n]+?)\$/g, (_, expression) => {
      // Additional check: if expression looks like a number pattern, don't process as math
      if (/^[\d,.\s\\!+-]+$/.test(expression.trim())) {
        return `$${expression}$`;
      }

      try {
        const html = katex.renderToString(expression.trim(), {
          displayMode: false,
          throwOnError: false,
          output: 'html'
        });
        return `<span>${html}</span>`;
      } catch (error) {
        console.error('KaTeX error:', error);
        return `$${expression}$`; // Return original format on error
      }
    });

    return input;
  };

  let processedText = processMarkdown(text);

  // Add styling to citation links if they were processed
  if (citationsMap) {
    processedText = processedText.replace(
      /<a href="([^"]+)">\[(\d+)\]<\/a>/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #2D7CD6; text-decoration: none;">[$2]</a>'
    );
  }

  // ------------------------------------------------------------------
  // Vertical-rhythm normalization (runs LAST, over the final HTML).
  // The wrapper renders with NORMAL white-space (not pre-wrap), so the
  // ONLY vertical space comes from margins we set here - no invisible
  // newline stacking can ever return. Source newlines are converted
  // explicitly: newlines touching a block element are swallowed (the
  // block's own margin is the single source of space), paragraph
  // breaks become one fixed 10px spacer, single newlines become <br>.
  // All generated HTML upstream is single-line, so every \n reaching
  // this point is a genuine source newline.
  // ------------------------------------------------------------------
  processedText = processedText
    // whitespace-only lines are empty lines
    .replace(/\n[ \t]+\n/g, '\n\n')
    // no blank lines before a block element
    .replace(/\n+[ \t]*(<(?:h[1-6]|div|hr|ul|ol|table|blockquote)\b)/g, '$1')
    // no blank lines after a block element
    .replace(/(<\/(?:h[1-6]|div|ul|ol|table|blockquote)>|<hr[^>]*>)[ \t]*\n+/g, '$1')
    // no leading/trailing empty lines in a fragment
    .replace(/^\n+/, '')
    .replace(/\n+$/, '')
    // paragraph break = exactly 10px, not a full blank text line
    .replace(/\n{2,}/g, '<span style="display:block;height:10px;"></span>')
    // intra-paragraph line break
    .replace(/\n/g, '<br>');

  return processedText;
};